<template>
  <nav
    :class="{ 'sidebar': true, 'is-loaded': isLoaded }"
    @mouseleave="cursorIsOutOfNav">
    <ul
      class="sidebar-level0"
      key="main-menu">
      <li
        :class="{ 'is-active': currentItem === 'home' }"
        @click="setCurrentItem('home')">
        <router-link to="/" :title="$t('sidebar.home')">
          <icon name="home" size="m" primaryColor="color-light" />
          <span
            v-if="waitingInquiries && userCanAddReservation"
            class="waiting-badge">
            {{ waitingInquiries }}
          </span>
          <span
            v-if="reservationsDisabled"
            class="warning-badge">
            !
          </span>
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('calendar') || currentUserIsAllowedToSee('history') || currentUserIsAllowedToSee('attendances_list') || currentUserIsAllowedToSee('waiting_list') || currentUserIsAllowedToSee('surveys')"
        :class="{ 'is-active': currentItem === 'calendar' }">
        <a
          href="#"
          @click.prevent.stop="setCurrentItem('calendar')">
          <icon name="calendar-date" size="m" primaryColor="color-light" />
        </a>
      </li>

      <li
        v-if="(currentUserIsAllowedToSee('payment') && currentUserIsAllowedToSee('invoices')) && userCanSeeFullPaymentsData"
        :class="{ 'is-active': currentItem === 'payment' }">
        <a
          href="#"
          @click.prevent.stop="setCurrentItem('payment')">
          <icon
            name="cart"
            size="m"
            primaryColor="color-light" />
        </a>
      </li>

      <li
        v-if="(currentUserIsAllowedToSee('payment') && !currentUserIsAllowedToSee('invoices')) && userCanSeeFullPaymentsData"
        :class="{ 'is-active': currentItem === 'payment' }">
        <router-link
          to="/payments"
          @click.native="setCurrentItem('payment')">
          <icon
            size="m"
            name="cart"
            primaryColor="color-light" />
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('stats')"
        :class="{ 'is-active': currentItem === 'stats' }">
        <router-link
          to="/statistics"
          :title="$t('sidebar.stats')"
          @click.native="setCurrentItem('stats')">
          <icon name="chart" size="m" primaryColor="color-light" />
        </router-link>
      </li>

      <li
        v-if="userIsWorker || currentUserIsAllowedToSee('company_details') || currentUserIsAllowedToSee('customers') || currentUserIsAllowedToSee('workers') || currentUserIsAllowedToSee('services') || currentUserIsAllowedToSee('surveys') || currentUserIsAllowedToSee('my_page') || currentUserIsAllowedToSee('stats')"
        :class="{ 'is-active': currentItem === 'company' }">
        <a
          href="#"
          @click.prevent.stop="setCurrentItem('company')">
          <icon name="suitcase" size="m" primaryColor="color-light" />

          <span
            v-if="expirationAlert"
            class="error-badge"
            :title="$t('sidebar.yourPackageExpiresIn') + expirationDays + (expirationDays === 1 ? $t('sidebar.day') : $t('sidebar.days'))">
            !
          </span>
        </a>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('reservation_rules') || currentUserIsAllowedToSee('company_rules') || currentUserIsAllowedToSee('can_manege_exceptions') || currentUserIsAllowedToSee('can_manage_worker_exceptions') || currentUserIsAllowedToSee('price_rules') || currentUserIsAllowedToSee('discounts') || currentUserIsAllowedToSee('billing')"
        :class="{ 'is-active': currentItem === 'rules' }">
        <a
          href="#"
          @click.prevent.stop="setCurrentItem('rules')">
          <icon name="switches" size="m" primaryColor="color-light" />
        </a>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('settings') || currentUserIsAllowedToSee('reservation_parameters') || currentUserIsAllowedToSee('integrations')"
        :class="{ 'is-active': currentItem === 'settings' }">
        <a
          href="#"
          @click.prevent.stop="setCurrentItem('settings')">
          <icon name="trigger-big" size="m" primaryColor="color-light" />
        </a>
      </li>
    </ul>

    <ul
      v-if="currentItem === 'calendar' && submenuIsOpened()"
      class="sidebar-level1"
      key="submenu-calendar">
      <li class="item-heading">
        {{ $t('sidebar.yourReservations') }}
      </li>

      <li
        v-if="currentUserIsAllowedToSee('calendar')"
        class="item"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'calendar' }">
        <router-link
          to="/calendar"
          @click.native="setCurrentMenuItem('calendar')">
          <icon
            size="s"
            name="calendar-big"
            primaryColor="color-light" />
          {{ $t('sidebar.calendar') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('history')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'history' }">
        <router-link
          to="/history"
          @click.native="setCurrentMenuItem('history')">
          <icon
            size="s"
            name="fold"
            primaryColor="color-light" />
          {{ $t('sidebar.history') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('history')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'blocked-termins' }">
        <router-link
          to="/blocked-termins"
          @click.native="setCurrentMenuItem('blocked-termins')">
          <icon
            size="s"
            name="lock"
            primaryColor="color-light" />
          {{ $t('sidebar.blockedTermins') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('sms_history')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'sms-history' }">
        <router-link
          to="/sms-history"
          @click.native="setCurrentMenuItem('sms-history')">
          <icon
            size="s"
            name="mailbox"
            primaryColor="color-light" />
          {{ $t('sidebar.smsHistory') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('attendances_list')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'attendance' }">
        <router-link
          to="/attendance"
          @click.native="setCurrentMenuItem('attendance')">
          <icon
            size="s"
            name="ok"
            primaryColor="color-light" />
          {{ $t('sidebar.attendances') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('waiting_list')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'waiting-list' }">
        <router-link
          to="/waiting-list"
          @click.native="setCurrentMenuItem('waiting-list')">
          <icon
            size="s"
            name="list"
            primaryColor="color-light" />
          {{ $t('sidebar.waitingList') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('surveys')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'surveys-filled' }">
        <router-link
          to="/surveys-filled"
          @click.native="setCurrentMenuItem('surveys-filled')">
          <icon
            size="s"
            name="questionnaire-filled"
            primaryColor="color-light" />
          {{ $t('sidebar.surveysFilled') }}
        </router-link>
      </li>
    </ul>

    <ul
      v-if="currentItem === 'payment' && currentUserIsAllowedToSee('payment') && currentUserIsAllowedToSee('invoices') && userCanSeeFullPaymentsData && submenuIsOpened()"
      class="sidebar-level1"
      key="submenu-payment">
      <li class="item-heading">
        {{ $t('sidebar.payments') }}
      </li>

      <li
        v-if="currentUserIsAllowedToSee('payment') && userCanSeeFullPaymentsData"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'payments' }">
        <router-link
          to="/payments"
          @click.native="setCurrentMenuItem('payments')">
          <icon
            size="s"
            name="cart"
            primaryColor="color-light" />
          {{ $t('sidebar.payments') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('invoices') && userCanSeeFullPaymentsData"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'invoices' }">
        <router-link
          to="/invoices"
          @click.native="setCurrentMenuItem('invoices')">
          <icon
            size="s"
            name="fold"
            primaryColor="color-light" />
          {{ $t('sidebar.invoices') }}
        </router-link>
      </li>
    </ul>

    <ul
      v-if="currentItem === 'company' && submenuIsOpened()"
      class="sidebar-level1"
      key="submenu-company">
      <li class="item-heading">
        <template v-if="!userIsWorker && !userIsReceptionist">{{ $t('sidebar.yourCompany') }}</template>
        <template v-else>{{ $t('sidebar.yourProfile') }}</template>
      </li>

      <li
        v-if="userIsWorker || userIsReceptionist"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'your-profile' }">
        <router-link
          to="/your-profile"
          @click.native="setCurrentMenuItem('your-profile')">
          <icon
            size="s"
            name="profile-fill"
            primaryColor="color-light" />
          {{ $t('sidebar.yourProfile') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('company_details')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'company' }">
        <router-link
          to="/company/"
          @click.native="setCurrentMenuItem('company')">
          <icon
            size="s"
            name="suitcase"
            primaryColor="color-light" />
          {{ $t('sidebar.company') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('customers')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'customers' }">
        <router-link
          to="/customers/"
          @click.native="setCurrentMenuItem('customers')">
          <icon
            size="s"
            name="profile-big"
            primaryColor="color-light" />
          {{ $t('sidebar.clients') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('workers')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'workers' }">
        <router-link
          to="/workers"
          @click.native="setCurrentMenuItem('workers')">
          <icon
            size="s"
            name="profile-fill"
            primaryColor="color-light" />
          {{ $t('sidebar.workers') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('services')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'services' }">
        <router-link
          to="/services"
          @click.native="setCurrentMenuItem('services')">
          <icon
            size="s"
            name="layers-bold"
            primaryColor="color-light" />
          {{ $t('sidebar.services') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('surveys')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'surveys' }">
        <router-link
          to="/surveys"
          @click.native="setCurrentMenuItem('surveys')">
          <icon
            size="s"
            name="questionnaire"
            primaryColor="color-light" />
          {{ $t('sidebar.surveys') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('my_page')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'my-page' }">
        <router-link
          to="/my-page"
          @click.native="setCurrentMenuItem('my-page')">
          <icon
            size="s"
            name="network"
            primaryColor="color-light" />
          {{ $t('sidebar.myPage') }}
        </router-link>
      </li>

      <!--
      <li
        v-if="currentUserIsAllowedToSee('stats')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'stats' }">
        <router-link
          to="/"
          @click.native="setCurrentMenuItem('stats')">
          <icon
            size="s"
            name="chart"
            primaryColor="color-light" />
          Statystyki
        </router-link>
      </li>
      -->

      <li
        v-if="currentUserIsAllowedToSee('billing')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'billing' }">
        <router-link
          to="/billing"
          @click.native="setCurrentMenuItem('billing')">
          <icon
            size="s"
            name="paperclip"
            primaryColor="color-light" />
          {{ $t('sidebar.billing') }}

          <span
            v-if="expirationAlert"
            class="error-badge"
            :title="$t('sidebar.yourPackageExpiresIn') + expirationDays + (expirationDays === 1 ? $t('sidebar.day') : $t('sidebar.days'))">
            !
          </span>
        </router-link>
      </li>
    </ul>

    <ul
      v-if="currentItem === 'rules' && submenuIsOpened()"
      class="sidebar-level1"
      key="submenu-rules">
      <li class="item-heading">
        {{ $t('sidebar.rules') }}
      </li>

      <li
        v-if="currentUserIsAllowedToSee('reservation_rules')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'reservation-rules' }">
        <router-link
          to="/rules/reservations/"
          @click.native="setCurrentMenuItem('reservation-rules')">
          <icon
            size="s"
            name="switches"
            primaryColor="color-light" />
          {{ $t('sidebar.reservationRules') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('company_rules') || currentUserIsAllowedToSee('can_manege_exceptions') || currentUserIsAllowedToSee('can_manage_worker_exceptions')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'opening-hours' }">
        <router-link
          to="/rules/opening-hours/"
          @click.native="setCurrentMenuItem('opening-rules')">
          <icon
            size="s"
            name="time"
            primaryColor="color-light" />
          {{ $t('sidebar.openingRules') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('price_rules')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'price-rules' }">
        <router-link
          to="/rules/prices/"
          @click.native="setCurrentMenuItem('price-rules')">
          <icon
            size="s"
            name="fold"
            primaryColor="color-light" />
          {{ $t('sidebar.priceRules') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('discounts')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'discounts' }">
        <router-link
          to="/discounts/"
          @click.native="setCurrentMenuItem('discounts')">
          <icon
            size="s"
            name="cart"
            primaryColor="color-light" />
          {{ $t('sidebar.discounts') }}
        </router-link>
      </li>
    </ul>

    <ul
      v-if="currentItem === 'settings' && submenuIsOpened()"
      class="sidebar-level1"
      key="submenu-settings">
      <li class="item-heading">
        {{ $t('sidebar.config') }}
      </li>

      <li
        v-if="currentUserIsAllowedToSee('settings')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'settings' }">
        <router-link
          to="/settings"
          @click.native="setCurrentMenuItem('settings')">
          <icon
            size="s"
            name="cog"
            primaryColor="color-light" />
          {{ $t('sidebar.settings') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('reservation_parameters')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'reservation-parameters' }">
        <router-link
          to="/reservation/parameters/"
          @click.native="setCurrentMenuItem('reservation-parameters')">
          <icon
            size="s"
            name="calendar-date"
            primaryColor="color-light" />
          {{ $t('sidebar.reservationParams') }}
        </router-link>
      </li>

      <li
        v-if="currentUserIsAllowedToSee('integrations')"
        :class="{ 'item': true, 'is-active': currentMenuItem === 'integrations' }">
        <router-link
          to="/integrations/"
          @click.native="setCurrentMenuItem('integrations')">
          <icon
            size="s"
            name="network"
            primaryColor="color-light" />
          {{ $t('sidebar.integrations') }}
        </router-link>
      </li>
    </ul>
  </nav>
</template>

<script>
import Configuration from './../mixins/Configuration';
import Responsive from './../mixins/Responsive';
import UserTypes from './../mixins/UserTypes';

export default {
  name: 'sidebar',
  mixins: [
    Configuration,
    Responsive,
    UserTypes
  ],
  data () {
    return {
      isLoaded: false,
      currentItem: 'home',
      currentMenuItem: null,
      expirationAlert: false,
      expirationDays: 0,
      submenuOpened: false,
      lastLevel1: null,
      lastLevel2: null
    };
  },
  computed: {
    waitingInquiries () {
      return this.$store.state.global.waitingInquiries.length;
    },
    reservationsDisabled () {
      return this.$store.state.config.company.status === 1;
    }
  },
  watch: {
    $route (to, from) {
      this.lastLevel1 = to.meta.sidebarLevel1;
      this.lastLevel2 = to.meta.sidebarLevel2;
      this.setCurrentItem(to.meta.sidebarLevel1);
      this.setCurrentMenuItem(to.meta.sidebarLevel2);
    }
  },
  mounted () {
    this.setCurrentItem(this.$route.meta.sidebarLevel1);
    this.setCurrentMenuItem(this.$route.meta.sidebarLevel2);
    this.$bus.$on('configuration-loaded', this.configurationLoad);
    this.$bus.$on('expiration-date-update', this.updateExpirationDate);
  },
  methods: {
    configurationLoad () {
      this.isLoaded = true;
      this.expirationDays = this.$store.state.config.company.expires_days;

      if (this.expirationDays <= 14 && !this.userIsWorker && !this.userIsReceptionist) {
        this.expirationAlert = true;
      }
    },
    updateExpirationDate (expirationDays) {
      this.expirationDays = expirationDays;

      if (this.expirationDays <= 14 && !this.userIsWorker && !this.userIsReceptionist) {
        this.expirationAlert = true;
      } else if (this.expirationDays > 14) {
        this.expirationAlert = false;
      }
    },
    setCurrentItem (newValue) {
      this.currentItem = newValue;
      this.$bus.$emit('body-clicked');

      if (window.matchMedia('(max-width: 1680px)').matches) {
        this.submenuOpened = true;
      }
    },
    setCurrentMenuItem (newValue) {
      this.currentMenuItem = newValue;

      if (window.matchMedia('(max-width: 1680px)').matches) {
        this.submenuOpened = false;
      }
    },
    submenuIsOpened () {
      if (window.matchMedia('(max-width: 1680px)').matches) {
        return this.submenuOpened;
      }

      return true;
    },
    cursorIsOutOfNav () {
      this.setCurrentItem(this.lastLevel1);
      this.setCurrentMenuItem(this.lastLevel2);
    }
  },
  beforeDestroy () {
    this.$bus.$off('configuration-loaded', this.configurationLoad);
    this.$bus.$off('expiration-date-update', this.updateExpirationDate);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.sidebar {
  background: $color-bg-dark;
  display: flex;
  height: calc(100% - 7rem);
  max-width: 23.4rem;
  position: fixed;
  top: 7rem;
  width: auto;
  z-index: 101;

  ul {
    opacity: 0;
    transition: opacity .5s ease-out;
    will-change: opacity;
  }

  &.is-loaded {
    ul {
      opacity: 1;
    }
  }

  &-level0 {
    background: $color-bg-darkest;
    list-style-type: none;
    margin: 0;
    padding: 0;
    width: 5.4rem;

    & > li {
      height: 6.4rem;

      & > a {
        align-items: center;
        display: flex;
        height: 6.4rem;
        justify-content: center;
        position: relative;
        width: 5.4rem;

        & > svg {
          opacity: .5;
        }

        .error-badge,
        .waiting-badge,
        .warning-badge {
          background: $color-notice;
          border: 2px solid $color-dark;
          border-radius: .9rem;
          color: $color-light;
          font-size: 1.3rem;
          font-weight: bold;
          height: 2.2rem;
          line-height: 1.9rem;
          position: absolute;
          right: .5rem;
          text-align: center;
          top: .75rem;
          width: 2.2rem;
        }

        .error-badge {
          background: $color-danger;
          border: 2px solid $color-dark;
        }

        .warning-badge {
          background: $color-warning;
          top: 3.2rem;
        }
      }

      &.is-active {
        background: $color-bg-dark;

        & > a {
          & > svg {
            opacity: 1;
          }
        }
      }
    }
  }

  &-level1 {
    background: $color-bg-dark;
    height: 100%;
    left: 5.4rem;
    list-style-type: none;
    margin: 0;
    padding: 2.4rem 2rem;
    width: 18rem;
    z-index: 10;

    .item {
      a {
        align-items: center;
        display: flex;
        font-size: $font-size-small;
        font-weight: 600;
        height: 4.8rem;
        margin: .2rem -1rem;
        padding: 0 1rem;
        position: relative;
        transition: all .25s ease-out;

        svg {
          margin-right: 1.5rem;
        }

        &:hover {
          background: rgba(255, 255, 255, .075);
          border-radius: $border-radius-small;
          color: $color-light;
        }

        .error-badge {
          background: $color-danger;
          border: none;
          border-radius: .6rem;
          color: $color-light;
          font-size: 1.3rem;
          font-weight: bold;
          height: 1.8rem;
          line-height: 1.9rem;
          position: absolute;
          right: 1rem;
          text-align: center;
          top: 50%;
          transform: translateY(-50%);
          width: 1.8rem;
        }
      }

      &.is-active {
        a {
          background: rgba(255, 255, 255, .075);
          border-radius: $border-radius-small;
          color: $color-light;
        }
      }
    }

    .item-heading {
      color: $color-text-light;
      font-size: $font-size-x-small;
      font-weight: 600;
      margin: 0 0 1.5rem 0;
      text-transform: uppercase;
    }
  }
}

@media (max-width: 1820px) {
  .sidebar {
    max-width: 5.4rem;

    &-level1 {
      opacity: 0!important;
      pointer-events: none;
      position: absolute;
      transition-duration: .125s;
    }

    &:hover {
      .sidebar-level1 {
        opacity: 1!important;
        pointer-events: auto;
      }
    }
  }
}

@media (max-width: 1040px) {
  .sidebar {
    &-level0 {
      & > li {
        height: 6.4rem;

        &.only-desktop {
          display: none;
        }
      }
    }

    &-level1 {
      & > li {
        &.only-desktop {
          display: none;
        }
      }
    }
  }
}

@media (max-width: 640px) {
  .sidebar {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/Sidebar.vue